<template>
  <section>
    <v-card class="mb-4">
      <v-card-title class="register-card-title">Benutzerinfo: Zugriffsmethoden</v-card-title>
      <v-card-text class="register-card-content">
        <v-table>
          <thead>
            <tr>
              <th scope="col">Methode</th>
              <th scope="col">Einfach erklärt</th>
              <th scope="col">Für wen?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row"><span class="access-label access-password">1 · Benutzername + Passwort</span></th>
              <td>Du gibst deine Zugangsdaten ein.</td>
              <td>Menschen, die die App benutzen</td>
            </tr>
            <tr>
              <th scope="row"><span class="access-label access-passkey">2 · Passkey / WebAuthn</span></th>
              <td>Du bestätigst die Anmeldung mit Fingerabdruck, Gesichtserkennung, Windows Hello oder einem Sicherheitsschlüssel.</td>
              <td>Menschen, die ohne Eingabe des App-Passworts einloggen möchten</td>
            </tr>
            <tr>
              <th scope="row"><span class="access-label access-oauth">3 · OAuth-Client</span></th>
              <td>Ein Programm erhält eine eigene Kennung und ein geheimes Passwort („Client Secret“). Damit holt es einen Zugangstoken für API-Aufrufe.</td>
              <td>Automatische Jobs, Skripte oder andere Programme</td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>

    <v-card class="mb-4 access-card access-password">
      <v-card-title class="register-card-title"><span class="access-section-label">1 · Benutzername + Passwort</span>Passwort ändern</v-card-title><v-card-text class="register-card-content"><PasswordChangeForm /></v-card-text>
    </v-card>

    <v-card class="mb-4 access-card access-password">
      <v-card-title class="register-card-title"><span class="access-section-label">1 · Benutzername + Passwort</span>Neuen Benutzer anlegen</v-card-title>

      <v-card-text class="register-card-content">
        <form @submit.prevent="submit" autocomplete="off">

          <div class="mb-3">
            <label class="form-label">Benutzername:</label>
            <input
              type="text"
              v-model="user.user_username"
              class="form-control"
              autocomplete="off"
              required
            />
          </div>

          <div class="mb-3">
            <label class="form-label">Full Name:</label>
            <input
              type="text"
              v-model="user.full_name"
              class="form-control"
              autocomplete="off"
              required
            />
          </div>

          <div class="mb-3">
            <label class="form-label">User UUID:</label>
            <input
              type="text"
              v-model="user.user_UUID"
              class="form-control"
              autocomplete="off"
              required
            />
          </div>

          <div class="mb-3">
            <label class="form-label">Android Seriennummer:</label>
            <input
              type="text"
              v-model="user.user_Seriennummer_Android"
              class="form-control"
              autocomplete="off"
              required
            />
          </div>

          <div class="mb-3">
            <label class="form-label">Passwort:</label>
            <input
              type="password"
              v-model="user.password"
              class="form-control"
              autocomplete="new-password"
              required
            />
          </div>

          <div class="register-actions">
            <v-btn color="primary" variant="flat" type="submit">Benutzer anlegen</v-btn>
          </div>

        </form>
      </v-card-text>
    </v-card>

    <v-card class="mb-4 access-card access-passkey">
      <v-card-title class="register-card-title"><span class="access-section-label">2 · Passkey / WebAuthn</span>WebAuthn-Gerät registrieren</v-card-title>

      <v-card-text class="register-card-content">
        <p>
          Registriere ein zusätzliches Anmeldegerät für deinen Benutzer – z.&nbsp;B.
          <strong>Smartphone</strong>, <strong>Sicherheitsschlüssel (FIDO2)</strong>,
          <strong>Windows Hello</strong> oder <strong>Touch ID / Face ID</strong>.
        </p>

        <div class="mb-3">
          <label class="form-label">Benutzername (aktueller User):</label>
          <input
            type="text"
            :value="currentUsername"
            class="form-control"
            readonly
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Gerätename (optional):</label>
          <input
            type="text"
            v-model="friendlyDeviceName"
            class="form-control"
            placeholder="z.B. 'Windows-PC Büro', 'Android-Smartphone', 'YubiKey USB-C'"
          />
          <small class="text-muted">
            Dieser Name hilft dir später, deine Geräte in der Liste zu erkennen.
          </small>
        </div>

        <div class="register-actions">
          <v-btn
            color="primary"
            variant="flat"
            @click="registerDevice"
            :disabled="!stateUser.user_username"
          >
            WebAuthn-Gerät registrieren
          </v-btn>
        </div>

        <!-- Anzeige der registrierten Credential-ID des zuletzt hinzugefügten Geräts -->
        <div v-if="registeredDeviceId" class="mt-4">
          <label class="form-label">Zuletzt registriertes Gerät (Credential-ID):</label>
          <input
            type="text"
            :value="registeredDeviceId"
            class="form-control"
            readonly
          />
        </div>
      </v-card-text>
    </v-card>

    <v-card class="mb-4 access-card access-passkey">
      <v-card-title class="register-card-title"><span class="access-section-label">2 · Passkey / WebAuthn</span>Registrierte WebAuthn-Geräte</v-card-title>

      <v-card-text class="register-card-content">
        <div v-if="devicesLoading">
          Geräte werden geladen …
        </div>

        <div v-else-if="!webauthnDevices || webauthnDevices.length === 0">
          <p>Es sind noch keine WebAuthn-Geräte registriert.</p>
        </div>

        <div v-else>
        <v-list dense>
          <v-list-item
            v-for="dev in webauthnDevices"
            :key="dev.id"
          >
            <div>
              <v-list-item-title>
                {{ dev.friendly_name || ('Gerät #' + dev.id) }}
                <span v-if="!dev.is_active" class="text-danger">(inaktiv)</span>
              </v-list-item-title>
              <v-list-item-subtitle>
                Credential-ID: {{ dev.credential_id }}<br />
                Erstellt: {{ new Date(dev.created_at).toLocaleString() }}
                <span v-if="dev.last_used_at">
                  – Letzte Nutzung: {{ new Date(dev.last_used_at).toLocaleString() }}
                </span>
              </v-list-item-subtitle>
            </div>

            <!-- NEU: Löschen-Button -->
            <v-list-item-action>
              <v-btn
                icon
                variant="text"
                color="error"
                @click="deleteDevice(dev)"
                :title="'Gerät löschen: ' + (dev.friendly_name || dev.credential_id)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        </div>
      </v-card-text>
    </v-card>

    <v-card class="mb-4 access-card access-oauth">
  <v-card-title class="register-card-title"><span class="access-section-label">3 · OAuth-Client</span>OAuth Client erstellen (Batch/Service Account)</v-card-title>

  <v-card-text class="register-card-content">
    <p class="mb-3">
      Erstellt einen OAuth2-Client, der <strong>nur dem eingeloggten User gehört</strong>.
      Das erzeugte <strong>Client-Secret wird nur einmal angezeigt</strong> – bitte sofort speichern.
    </p>

    <form @submit.prevent="createOAuthClient" autocomplete="off">
      <div class="mb-3">
        <label class="form-label">Client ID (eindeutig):</label>
        <input
          type="text"
          v-model="oauthClient.client_id"
          class="form-control"
          placeholder="z.B. svc-reporting"
          required
        />
        <small class="text-muted">
          Erlaubte Zeichen: Buchstaben/Zahlen sowie . _ - (keine Leerzeichen).
        </small>
      </div>

      <div class="mb-3">
        <label class="form-label">Client Name (optional):</label>
        <input
          type="text"
          v-model="oauthClient.client_name"
          class="form-control"
          placeholder="z.B. Reporting Job"
        />
      </div>

      <div class="mb-3">
        <label class="form-label">Auth Methode:</label>
        <select v-model="oauthClient.auth_method" class="form-control">
          <option value="client_secret_basic">client_secret_basic (empfohlen)</option>
          <option value="client_secret_post">client_secret_post</option>
        </select>
      </div>

      <div class="register-actions">
        <v-btn
          color="primary"
          variant="flat"
          type="submit"
          :loading="oauthClientLoading"
          :disabled="!stateUser.user_username || oauthClientLoading"
        >
          {{ oauthClientLoading ? 'Erstelle…' : 'Client erstellen' }}
        </v-btn>
      </div>
    </form>

    <!-- Ergebnisanzeige -->
    <div v-if="oauthClientResult" class="mt-4">
      <v-alert type="success" dense>
        Client wurde erstellt. Secret wird nur einmal angezeigt – jetzt speichern!
      </v-alert>

      <div class="mb-2">
        <label class="form-label">client_id:</label>
        <input class="form-control" :value="oauthClientResult.client_id" readonly />
      </div>

      <div class="mb-2">
        <label class="form-label">auth_method:</label>
        <input class="form-control" :value="oauthClientResult.auth_method" readonly />
      </div>

      <div class="mb-2">
        <label class="form-label">client_secret (nur einmal):</label>

        <div class="register-secret">
          <input
            class="form-control"
            :type="showClientSecret ? 'text' : 'password'"
            :value="oauthClientResult.client_secret"
            readonly
          />
          <v-btn size="small" color="primary" variant="outlined" @click="showClientSecret = !showClientSecret">
            {{ showClientSecret ? 'Ausblenden' : 'Anzeigen' }}
          </v-btn>
          <v-btn size="small" color="primary" variant="outlined" @click="copyClientSecret">
            Kopieren
          </v-btn>
        </div>
      </div>

      <div class="mt-3">
        <small class="text-muted">
          Token holen (Beispiel):
          <code>
            curl -u '{{oauthClientResult.client_id}}:{{oauthClientResult.client_secret}}'
            -d 'grant_type=client_credentials'
            {{ axiosBaseHint }}/oauth/token
          </code>
        </small>
      </div>
    </div>

    <div v-if="oauthClientError" class="mt-4">
      <v-alert type="error" dense>
        {{ oauthClientError }}
      </v-alert>
    </div>
  </v-card-text>
</v-card>

    <v-card class="mb-4 access-card access-oauth">
  <v-card-title class="register-card-title"><span class="access-section-label">3 · OAuth-Client</span>Registrierte OAuth Clients</v-card-title>

  <v-card-text class="register-card-content">
    <div class="register-toolbar">
      <v-btn
        color="primary"
        variant="flat"
        :loading="oauthClientsLoading"
        @click="loadOAuthClients"
        :disabled="!stateUser.user_username || oauthClientsLoading"
      >
        {{ oauthClientsLoading ? 'Lade…' : 'Aktualisieren' }}
      </v-btn>

      <small class="text-muted" v-if="stateUser.user_username">
        Es werden nur Clients des eingeloggten Users angezeigt.
      </small>
    </div>

    <div v-if="oauthClientsLoading">
      OAuth Clients werden geladen …
    </div>

    <div v-else-if="oauthClientsError">
      <v-alert type="error" dense>
        {{ oauthClientsError }}
      </v-alert>
    </div>

    <div v-else-if="!oauthClients || oauthClients.length === 0">
      <p>Es sind noch keine OAuth Clients registriert.</p>
    </div>

    <div v-else>
      <v-list dense>
        <v-list-item
          v-for="c in oauthClients"
          :key="c.client_id"
        >
          <div>
            <v-list-item-title>
              {{ c.client_id }}
              <span v-if="c.client_name"> — {{ c.client_name }}</span>
              <span v-if="c.is_active === false" class="text-danger"> (inaktiv)</span>
            </v-list-item-title>

            <v-list-item-subtitle>
              auth_method: {{ c.auth_method }}<br />
              <span v-if="c.created_at">
                Erstellt: {{ new Date(c.created_at).toLocaleString() }}<br />
              </span>
              <span v-if="c.last_used_at">
                Letzte Nutzung: {{ new Date(c.last_used_at).toLocaleString() }}
              </span>
              <span v-else>
                Letzte Nutzung: —
              </span>
            </v-list-item-subtitle>
          </div>

          <!-- optional: später Buttons für deaktivieren/löschen -->
          <!--
          <v-list-item-action>
            <v-btn icon @click="..."><v-icon>mdi-delete</v-icon></v-btn>
          </v-list-item-action>
          -->
        </v-list-item>
      </v-list>
    </div>
  </v-card-text>
</v-card>
  </section>
</template>

<script>
import PasswordChangeForm from './PasswordChangeForm.vue';
import { mapActions, mapGetters } from 'vuex';
import axios, { getApiBaseHint } from '@/api/client';


export default {
  components: { PasswordChangeForm },
  name: 'RegisterScreen',

  data() {
    return {
      user: {
        user_username: '',
        full_name: '',
        user_UUID: '',
        user_Seriennummer_Android: '',
        password: '',
      },

      passwords: {
        oldPassword: '',
        newPassword: '',
      },

      // hier merken wir uns die Credential-ID des registrierten Geräts
      registeredDeviceId: '',
      // Liste der WebAuthn-Geräte + Ladezustand
      webauthnDevices: [],
      devicesLoading: false,
      // Anzeigename für das Gerät
      friendlyDeviceName: '',
      oauthClient: {
          client_id: '',
          client_name: '',
          auth_method: 'client_secret_basic',
        },
        oauthClientLoading: false,
        oauthClientResult: null,
        oauthClientError: '',
        showClientSecret: false,
        axiosBaseHint: '',  // nur für die curl-hilfe
        oauthClients: [],
        oauthClientsLoading: false,
        oauthClientsError: '',
    };
  },

  computed: {
    ...mapGetters(['stateUser', 'isAuthenticated']),

    currentUsername() {
      console.log('computed currentUsername, stateUser =', this.stateUser);
      if (this.stateUser && this.stateUser.user_username) {
        return this.stateUser.user_username;
      }
      return '';
    },
  },

  async mounted() {
    // Nur zum Debuggen – aber NICHT currentUsername hier setzen!
    console.log('Bin register mounted, this.stateUser =', this.stateUser.user_username);

    if (this.stateUser && this.stateUser.user_username) {
      await this.loadWebauthnDevices();
      await this.loadOAuthClients(); // NEU
    }
  },

  methods: {
    ...mapActions(['register', 'logIn', 'logOut', 'viewMe']),

    setAxiosBaseUrl() {
      this.axiosBaseHint = getApiBaseHint();
    },

    async createOAuthClient() {
      try {
        this.oauthClientLoading = true;
        this.oauthClientError = '';
        this.oauthClientResult = null;
        this.showClientSecret = false;

        if (!this.stateUser || !this.stateUser.user_username) {
          this.oauthClientError = 'Kein eingeloggter Benutzer gefunden.';
          return;
        }

        this.setAxiosBaseUrl();

        // POST /oauth/clients (Server setzt owner_user automatisch aus Cookie/JWT)
        const resp = await axios.post('oauth/clients', {
          client_id: this.oauthClient.client_id,
          client_name: this.oauthClient.client_name || null,
          auth_method: this.oauthClient.auth_method || 'client_secret_basic',
        });

        this.oauthClientResult = resp.data;

        // Eingabefelder optional leeren (client_id eher nicht, falls man nochmal kopieren will)
        // this.oauthClient.client_id = '';
        // this.oauthClient.client_name = '';

        alert('OAuth Client erstellt. Secret jetzt speichern!');
      } catch (err) {
        console.error('Fehler beim Erstellen des OAuth Clients:', err);

        if (err.response && err.response.data) {
          // FastAPI detail handling
          if (typeof err.response.data.detail === 'string') {
            this.oauthClientError = err.response.data.detail;
          } else {
            this.oauthClientError = JSON.stringify(err.response.data);
          }
        } else {
          this.oauthClientError = 'Erstellen fehlgeschlagen.';
        }
      } finally {
        this.oauthClientLoading = false;
      }
    },

    async copyClientSecret() {
      try {
        const s = this.oauthClientResult?.client_secret;
        if (!s) return;

        await navigator.clipboard.writeText(s);
        alert('Client Secret kopiert.');
      } catch (e) {
        // Fallback (ältere Browser)
        try {
          const el = document.createElement('textarea');
          el.value = this.oauthClientResult.client_secret;
          document.body.appendChild(el);
          el.select();
          document.execCommand('copy');
          document.body.removeChild(el);
          alert('Client Secret kopiert.');
        } catch (e2) {
          alert('Kopieren nicht möglich – bitte manuell markieren.');
        }
      }
    },


    async loadOAuthClients() {
      try {
        this.oauthClientsLoading = true;
        this.oauthClientsError = '';

        if (!this.stateUser || !this.stateUser.user_username) {
          this.oauthClients = [];
          return;
        }

        this.setAxiosBaseUrl();

        const resp = await axios.get('oauth/clients');
        this.oauthClients = resp.data || [];
      } catch (err) {
        console.error('Fehler beim Laden der OAuth Clients:', err);
        this.oauthClients = [];

        if (err.response && err.response.data) {
          this.oauthClientsError = typeof err.response.data.detail === 'string'
            ? err.response.data.detail
            : JSON.stringify(err.response.data);
        } else {
          this.oauthClientsError = 'Laden fehlgeschlagen.';
        }
      } finally {
        this.oauthClientsLoading = false;
      }
    },


    async submit() {
      try {
        await this.register(this.user); // ruft intern logIn + viewMe
        this.$router.push('/dashboard');
      } catch (error) {
        throw 'Username already exists. Please try again.';
      }
    },

  async deleteDevice(dev) {
    if (!dev || !dev.id) {
      return;
    }

    const name = dev.friendly_name || `Credential #${dev.id}`;

    if (!confirm(`Möchtest du das Gerät "${name}" wirklich löschen?`)) {
      return;
    }

    try {
      await axios.delete(`webauthncredentials/${dev.id}`);

      // Liste neu laden
      await this.loadWebauthnDevices();

      // Falls gerade das zuletzt registrierte Gerät gelöscht wurde, Anzeige leeren
      if (this.registeredDeviceId === dev.credential_id) {
        this.registeredDeviceId = '';
      }

      alert(`Gerät "${name}" wurde gelöscht.`);
    } catch (err) {
      console.error('Fehler beim Löschen des Geräts:', err);
      if (err.response && err.response.data) {
        alert(`Löschen fehlgeschlagen: ${JSON.stringify(err.response.data)}`);
      } else {
        alert('Löschen fehlgeschlagen.');
      }
    }
  },

    async loadWebauthnDevices() {
      try {
        this.devicesLoading = true;
        const resp = await axios.get('webauthncredentials');
        this.webauthnDevices = resp.data || [];
      } catch (err) {
        console.error('Fehler beim Laden der WebAuthn-Geräte:', err);
        this.webauthnDevices = [];
      } finally {
        this.devicesLoading = false;
      }
    },


// --------------------------------------------------------
// WebAuthn-Registrierung eines Geräts (egal ob Handy, Key, Windows Hello, ...)
// --------------------------------------------------------
async registerDevice() {
  try {
    const username = this.currentUsername;

    if (!username) {
      alert("Kein eingeloggter Benutzer gefunden – bitte zuerst anmelden.");
      return;
    }

    if (!window.PublicKeyCredential) {
      alert("Dieser Browser unterstützt WebAuthn nicht.");
      return;
    }

    console.log("Starte WebAuthn-Registrierung für:", username);

    // 1) Registrierungs-Optionen holen
    const resp = await axios.get('webauthnregisterdata');
    const options = resp.data;

    console.log("WebAuthn options vom Server:", options);

    // --- Challenge konvertieren ---
    const challenge = this.base64urlToUint8Array(options.challenge);

    // --- User-ID konvertieren ---
    const userIdBytes = new TextEncoder().encode(options.user.id);

    // --- excludeCredentials konvertieren ---
    const excludeCredentials = (options.excludeCredentials || []).map((c) => ({
      type: c.type,
      id: this.base64urlToUint8Array(c.id),
    }));

    // --- publicKey erstellen ---
    const publicKey = {
      challenge,
      rp: options.rp,
      user: {
        ...options.user,
        id: userIdBytes,
      },
      pubKeyCredParams: options.pubKeyCredParams,
      excludeCredentials,

      // etwas defensiver:
      authenticatorSelection: {
        // nicht erzwingen, nur bevorzugen:
        userVerification: "preferred",
        // authenticatorAttachment weglassen, damit Browser selbst wählen darf
        // authenticatorAttachment: "platform",
      },

      // optional (meist ok):
      // residentKey: "preferred",
    };

    console.log("publicKey für WebAuthn create():", publicKey);

    // 2) Credential erzeugen
    let newCredential;
    try {
      console.log("navigator.userAgent =", navigator.userAgent);
      console.log("PublicKey options (Android):", publicKey);

      newCredential = await navigator.credentials.create({ publicKey });
    } catch (e) {
      console.error("WebAuthn error:", e, {
        name: e.name,
        message: e.message,
        code: e.code,
        stack: e.stack,
      });

      const msg = `${e.name}: ${e.message}`;
      alert(msg);  // zeigt dir exakt z.B. "NotReadableError: An unknown error while talking to the credential manager."

      return;
    }

    if (!newCredential) {
      alert("Die Registrierung wurde abgebrochen oder es wurde kein Credential erstellt.");
      return;
    }

    console.log("Credential vom Authenticator:", newCredential);

    // 3) CredentialResponse erzeugen
    const credentialResponse = {
      id: newCredential.id,
      rawId: this.arrayBufferToBase64url(newCredential.rawId),
      type: newCredential.type,
      response: {
        attestationObject: this.arrayBufferToBase64url(
          newCredential.response.attestationObject
        ),
        clientDataJSON: this.arrayBufferToBase64url(
          newCredential.response.clientDataJSON
        ),
      },
      // OPTIONAL: Friendly Name Richtung Backend mitgeben (wenn du das dort berücksichtigst)
      friendly_name: this.friendlyDeviceName || null,
    };

    console.log("credentialResponse (JSON):", credentialResponse);

    // 4) An Backend senden
    try {
      await axios.post("/webauthnregisterresponse", credentialResponse, { withCredentials: true });
      this.registeredDeviceId = credentialResponse.id;
      await this.loadWebauthnDevices();
      alert("Gerät erfolgreich registriert (oder war bereits registriert).");
    } catch (err) {
      console.error(err);
      if (err.response && err.response.data && err.response.data.detail) {
        alert(`Registrierung fehlgeschlagen: ${err.response.data.detail}`);
      } else {
        alert("Registrierung fehlgeschlagen.");
      }
    }

    // Erfolg
    this.registeredDeviceId = credentialResponse.id;
    // optional: Geräte-Liste neu laden
    await this.loadWebauthnDevices();

    // optional: Eingabefeld leeren
    this.friendlyDeviceName = '';

    alert("Gerät erfolgreich registriert!");
  } catch (err) {
    console.error(err);
    alert("Registrierung fehlgeschlagen.");
  }
},



    // --------------------------------------------------------
    // Hilfsfunktionen für Base64url <-> ArrayBuffer
    // --------------------------------------------------------
    base64urlToUint8Array(base64urlString) {
      let base64 = base64urlString.replace(/-/g, '+').replace(/_/g, '/');
      const pad = base64.length % 4;
      if (pad) {
        base64 += '='.repeat(4 - pad);
      }
      const raw = window.atob(base64);
      const bytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; ++i) {
        bytes[i] = raw.charCodeAt(i);
      }
      return bytes;
    },

    arrayBufferToBase64url(buffer) {
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      let base64 = window.btoa(binary);
      return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    },
  },
};
</script>

<style scoped>
.access-password { --access-color: #64b5f6; --access-tint: rgba(100, 181, 246, .12); }
.access-passkey { --access-color: #81c784; --access-tint: rgba(129, 199, 132, .12); }
.access-oauth { --access-color: #ce93d8; --access-tint: rgba(206, 147, 216, .12); }
.access-label { display: inline-block; border-left: 4px solid var(--access-color); background: var(--access-tint); padding: 8px 12px; border-radius: 4px; }
.access-card { --register-focus-color: #64b5f6; --register-option-background: #212121; border-left: 4px solid var(--access-color); }
.access-card .register-card-content { padding: 16px; }
.access-card form,
.access-card .mb-3,
.access-card .mb-2,
.register-secret { width: 100%; max-width: 520px; }
.form-label { display: block; margin-bottom: 6px; font-weight: 500; }
.form-control {
  display: block;
  box-sizing: border-box;
  width: 100%;
  max-width: 520px;
  min-height: 48px;
  padding: 10px 12px;
  border: 1px solid color-mix(in srgb, currentColor 45%, transparent);
  border-radius: 4px;
  background: color-mix(in srgb, currentColor 4%, transparent);
  color: inherit;
  font: inherit;
}
.form-control::placeholder { color: color-mix(in srgb, currentColor 60%, transparent); opacity: 1; }
.form-control:focus { outline: 2px solid var(--register-focus-color); outline-offset: -1px; }
.form-control[readonly] { background: color-mix(in srgb, currentColor 8%, transparent); }
select.form-control { appearance: auto; }
select.form-control option { background: var(--register-option-background); color: #fff; }
.access-card .mb-3 { margin-bottom: 16px !important; }
.access-card .mb-2 { margin-bottom: 16px !important; }
.access-card .text-muted { display: block; margin-top: 6px; color: color-mix(in srgb, currentColor 70%, transparent); }
.register-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 16px; }
.register-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 16px; }
.register-secret { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.register-secret .form-control { flex: 1 1 240px; min-width: 0; }
.access-card .register-card-title { background: var(--access-tint); white-space: normal; }
.access-section-label { display: block; color: var(--access-color); font-size: 12px; font-weight: 700; margin-bottom: 4px; }
</style>
